/* DesireMatrix homepage behaviour. Vanilla only - no libraries. */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Horizon puts overflow:hidden on html and body and scrolls an inner
     .page-wrapper instead, which means window.scrollY is permanently 0. Anything
     that reacts to scroll has to find the real scrolling element first. */
  function scrollRoot(el) {
    var p = el && el.parentElement;
    while (p && p !== document.body) {
      var oy = window.getComputedStyle(p).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
      p = p.parentElement;
    }
    var wrap = document.querySelector('.page-wrapper');
    if (wrap) {
      var wy = window.getComputedStyle(wrap).overflowY;
      if (wy === 'auto' || wy === 'scroll') return wrap;
    }
    return null; /* null means the window scrolls normally */
  }

  /* ---- 1. Announcement rotation. Height is reserved in CSS, so this never reflows. ---- */
  function announcement(root) {
    var msgs = root.querySelectorAll('[data-ann-msg]');
    if (msgs.length < 2 || reduce) return;
    var wait = parseInt(root.getAttribute('data-interval'), 10) || 6000;
    var i = 0, timer = null, held = false;
    function step() {
      if (held) return;
      msgs[i].classList.remove('is-on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-on');
    }
    function start() { stop(); timer = setInterval(step, wait); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    root.addEventListener('mouseenter', function () { held = true; stop(); });
    root.addEventListener('mouseleave', function () { held = false; start(); });
    root.addEventListener('focusin', function () { held = true; stop(); });
    root.addEventListener('focusout', function () { held = false; start(); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else if (!held) { start(); }
    });
    start();
  }

  /* ---- 2. Sticky header from 120px, 200ms slide-in.
         A spacer takes over the header's height the moment it goes fixed, so the
         transition costs nothing in CLS. ---- */
  function stickyHeader(head) {
    var root = scrollRoot(head);
    var target = root || window;
    var spacer = document.createElement('div');
    spacer.style.display = 'none';
    head.parentNode.insertBefore(spacer, head.nextSibling);
    var stuck = false, ticking = false;
    function offset() { return root ? root.scrollTop : window.scrollY; }
    function apply() {
      ticking = false;
      var should = offset() > 120;
      if (should === stuck) return;
      stuck = should;
      spacer.style.height = head.offsetHeight + 'px';
      spacer.style.display = should ? 'block' : 'none';
      head.classList.toggle('dm-head--stuck', should);
    }
    target.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(apply); }
    }, { passive: true });
    apply();
  }

  /* ---- 3. Overlays: search + mobile menu ---- */
  function overlay(panel, openers, closers, focusTarget) {
    if (!panel) return;
    var lock = scrollRoot(panel) || document.documentElement;
    var last = null;
    function positionBelowHeader() {
      var head = document.querySelector('[data-sticky-header]');
      var bottom = head ? Math.max(0, Math.round(head.getBoundingClientRect().bottom)) : 0;
      panel.style.setProperty('--dm-overlay-top', bottom + 'px');
    }
    function setOpenerState(on) {
      openers.forEach(function (b) {
        b.classList.toggle('is-open', on);
        b.setAttribute('aria-expanded', String(on));
        if (b.hasAttribute('data-menu-open')) {
          b.setAttribute('aria-label', on ? 'Close menu' : 'Open menu');
        }
      });
    }
    function open() {
      last = document.activeElement;
      positionBelowHeader();
      panel.classList.add('is-on');
      setOpenerState(true);
      lock.style.overflow = 'hidden';
      var t = focusTarget && panel.querySelector(focusTarget);
      if (t) t.focus();
    }
    function close() {
      panel.classList.remove('is-on');
      setOpenerState(false);
      lock.style.overflow = '';
      if (last && last.focus) last.focus();
    }
    function toggle() {
      if (panel.classList.contains('is-on')) close(); else open();
    }
    openers.forEach(function (b) { b.addEventListener('click', toggle); });
    closers.forEach(function (b) { b.addEventListener('click', close); });
    panel.addEventListener('click', function (e) { if (e.target === panel) close(); });
    window.addEventListener('resize', function () {
      if (panel.classList.contains('is-on')) positionBelowHeader();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-on')) close();
    });
  }

  /* ---- 4. Generic aria-expanded disclosure (mobile nav, footer, FAQ) ---- */
  function disclosure(btn) {
    var target = document.getElementById(btn.getAttribute('aria-controls'));
    if (!target) return;
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      target.hidden = open;
      /* focus deliberately stays on the trigger */
    });
  }

  /* ---- 5. Tabs. Full ARIA pattern with roving tabindex.
         Panels are never display:none - inactive ones are hidden with
         visibility/position in CSS so their markup stays crawlable. ---- */
  function tabs(root) {
    var list = root.querySelector('[role="tablist"]');
    if (!list) return;
    var btns = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
    function select(idx, moveFocus) {
      btns.forEach(function (b, n) {
        var on = n === idx;
        b.setAttribute('aria-selected', String(on));
        b.tabIndex = on ? 0 : -1;
        var p = document.getElementById(b.getAttribute('aria-controls'));
        if (!p) return;
        p.classList.toggle('is-on', on);
        if (on) { p.removeAttribute('aria-hidden'); } else { p.setAttribute('aria-hidden', 'true'); }
        p.querySelectorAll('img[loading]').forEach(function (img) {
          img.setAttribute('loading', on ? 'eager' : 'lazy');
        });
      });
      if (moveFocus) btns[idx].focus();
    }
    btns.forEach(function (b, n) {
      b.addEventListener('click', function () { select(n, false); });
      b.addEventListener('keydown', function (e) {
        var i = null;
        if (e.key === 'ArrowRight') i = (n + 1) % btns.length;
        else if (e.key === 'ArrowLeft') i = (n - 1 + btns.length) % btns.length;
        else if (e.key === 'Home') i = 0;
        else if (e.key === 'End') i = btns.length - 1;
        if (i === null) return;
        e.preventDefault();
        select(i, true);
      });
    });
    var start = btns.findIndex(function (b) { return b.getAttribute('aria-selected') === 'true'; });
    select(start < 0 ? 0 : start, false);
  }

  /* ---- boot ---- */
  function boot() {
    document.querySelectorAll('[data-ann]').forEach(announcement);
    document.querySelectorAll('[data-tabs]').forEach(tabs);
    document.querySelectorAll('[data-disclosure]').forEach(disclosure);

    var head = document.querySelector('[data-sticky-header]');
    if (head) stickyHeader(head);

    overlay(
      document.querySelector('[data-search-overlay]'),
      Array.prototype.slice.call(document.querySelectorAll('[data-search-open]')),
      Array.prototype.slice.call(document.querySelectorAll('[data-search-close]')),
      'input[type="search"]'
    );
    overlay(
      document.querySelector('[data-mobile-menu]'),
      Array.prototype.slice.call(document.querySelectorAll('[data-menu-open]')),
      Array.prototype.slice.call(document.querySelectorAll('[data-menu-close]')),
      null
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
